import { CloseRounded } from '@mui/icons-material';
import { type KeyboardEvent, useId, useMemo, useRef, useState } from 'react';
import { useBrandNames } from '../../hooks/useBrandNames';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import { BRAND_SEARCH_MIN_LENGTH } from '../../lib/constants';
import {
  Dropdown,
  DropdownEmpty,
  DropdownError,
  DropdownOption,
  MultiBox,
  TokenChip
} from './FilterRules.styles';

export const BRAND_SEARCH_SHORT_MESSAGE = `Type at least ${BRAND_SEARCH_MIN_LENGTH} characters to search brands`;
export const BRAND_SEARCH_ERROR_MESSAGE = 'Brand search failed. Try again.';

type BrandMultiSelectProps = {
  label: string;
  /** Selected brand ids. */
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  tone?: 'include' | 'exclude';
  /** Brand ids that cannot be picked here, such as the ones the other side of the rule holds. */
  unavailable?: string[];
  invalid?: boolean;
  disabled?: boolean;
};

/**
 * Searchable multi-select over proxima-platform brands, with the look and keys of `MultiSelect`.
 *
 * - Options come from `useBrandSearch` and show the brand name and domain, with the id as secondary text.
 * - Values stay brand ids, and chips show each brand's name from `useBrandNames`.
 * - A chip whose id has no name, such as a deleted brand, shows the raw id.
 * - Selected and `unavailable` ids are never offered.
 */
export const BrandMultiSelect = ({
  label,
  values,
  onChange,
  placeholder = 'Search brands…',
  tone = 'include',
  unavailable = [],
  invalid,
  disabled = false
}: BrandMultiSelectProps) => {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const brandName = useBrandNames(values);
  const { results, status, search } = useBrandSearch();

  const matches = useMemo(
    () =>
      status === 'done'
        ? results.filter(
            brand => !values.includes(brand.brandId) && !unavailable.includes(brand.brandId)
          )
        : [],
    [results, status, values, unavailable]
  );
  const optionId = (index: number) => `${listId}-option-${index}`;
  const active = open && matches[activeIndex] ? optionId(activeIndex) : undefined;

  const updateQuery = (text: string) => {
    setQuery(text);
    setActiveIndex(0);
    search(text);
  };
  const add = (brandId: string) => {
    onChange([...values, brandId]);
    updateQuery('');
    inputRef.current?.focus();
  };
  const remove = (brandId: string) => onChange(values.filter(item => item !== brandId));

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(index => Math.max(Math.min(index + 1, matches.length - 1), 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const match = matches[activeIndex];
      if (open && match) add(match.brandId);
    } else if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'Backspace' && !query && values.length > 0) {
      remove(values[values.length - 1]);
    }
  };

  const emptyMessage = () => {
    if (status === 'short') return <DropdownEmpty>{BRAND_SEARCH_SHORT_MESSAGE}</DropdownEmpty>;
    if (status === 'searching') return <DropdownEmpty>Searching…</DropdownEmpty>;
    if (status === 'error')
      return <DropdownError role="alert">{BRAND_SEARCH_ERROR_MESSAGE}</DropdownError>;
    return matches.length === 0 ? <DropdownEmpty>No matches</DropdownEmpty> : null;
  };

  return (
    <MultiBox
      focused={open}
      invalid={invalid}
      bare={disabled}
      onMouseDown={event => {
        if (!disabled && event.target === event.currentTarget) {
          event.preventDefault();
          inputRef.current?.focus();
        }
      }}
    >
      {values.map(brandId => {
        const name = brandName(brandId);
        return (
          <TokenChip key={brandId} tone={tone} title={name ? brandId : undefined}>
            {name ?? <code>{brandId}</code>}
            {disabled ? null : (
              <button
                type="button"
                aria-label={`Remove ${name ?? brandId}`}
                onClick={() => remove(brandId)}
              >
                <CloseRounded />
              </button>
            )}
          </TokenChip>
        );
      })}
      {disabled ? null : (
        <input
          ref={inputRef}
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active}
          placeholder={values.length === 0 ? placeholder : ''}
          value={query}
          onChange={event => {
            updateQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      )}
      {open && !disabled ? (
        <Dropdown id={listId} role="listbox" aria-label={label} aria-busy={status === 'searching'}>
          {emptyMessage()}
          {matches.map((brand, index) => (
            <DropdownOption
              key={brand.brandId}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              active={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              /* mousedown, not click: the input's blur would close the list first */
              onMouseDown={event => {
                event.preventDefault();
                add(brand.brandId);
              }}
            >
              {brand.domain ? `${brand.name} · ${brand.domain}` : brand.name}
              <small>{brand.brandId}</small>
            </DropdownOption>
          ))}
        </Dropdown>
      ) : null}
    </MultiBox>
  );
};
